<template>
  <div class="videos-page bg-base-200 min-h-screen p-4 md:p-8 relative">
    <div class="container mx-auto">
      <div
        class="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4"
      >
        <h1 class="text-4xl font-bold text-base-content text-center">Videos</h1>
        <div class="flex gap-2">
          <button
            v-if="loggedIn"
            class="btn btn-primary"
            @click="openNewVideoModal"
          >
            <i class="fas fa-plus mr-2"></i> Add Video
          </button>
          <a
            href="https://www.youtube.com/channel/UCtbI8FWG4a1As9wx1E4pYyQ"
            class="btn btn-error"
            target="_blank"
            rel="noopener"
          >
            Follow on Youtube <i class="fab fa-youtube ml-2"></i>
          </a>
        </div>
      </div>

      <div
        v-if="visibleVideos.length"
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
      >
        <div
          v-for="video in visibleVideos"
          :key="video.id"
          class="card bg-base-100 shadow-xl cursor-pointer group"
          @click="playVideo(video.url)"
        >
          <figure class="relative">
            <img
              :src="video.thumbnail"
              :alt="video.title"
              class="w-full h-48 object-cover"
            />
            <div
              class="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <i class="fas fa-play text-white text-5xl"></i>
            </div>
          </figure>
          <div class="card-body p-4">
            <h2 class="card-title text-base leading-snug">
              {{ video.title }}
            </h2>
            <div v-if="loggedIn" class="card-actions justify-end">
              <button
                class="btn btn-ghost btn-sm"
                @click.stop="editVideo(video)"
              >
                <i class="fas fa-pencil-alt"></i>
              </button>
              <button
                class="btn btn-ghost btn-sm"
                @click.stop="confirmDelete(video.id)"
              >
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center py-16">
        <p class="text-xl text-base-content opacity-70">No videos found.</p>
      </div>
    </div>

    <VideoModal
      v-if="showEditModal"
      :video="selectedVideo"
      :is-edit="isEdit"
      @close="closeModal"
      @save-success="showToast($event, 'success')"
      @save-error="showToast($event, 'error')"
    />
    <VideoPlayerModal
      v-if="videoToPlay"
      :video-url="videoToPlay"
      @close="videoToPlay = null"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useHead } from '@vueuse/head'
import { videosCollection } from '../firebase'
import { doc, deleteDoc } from 'firebase/firestore'
import VideoModal from '../components/VideoModal.vue'
import VideoPlayerModal from '../components/VideoPlayerModal.vue'
import { useToast } from '@/composables/useToast'

const store = useStore()

useHead({
  title: 'Videos - Sqwerv',
  meta: [
    {
      hid: 'sqwervVideosPage',
      name: 'description',
      content:
        'Sqwerv music videos and live performance features. Watch our latest content from YouTube.',
    },
  ],
})

const videos = computed(() => store.getters['videos/videos'])
const loggedIn = computed(() => store.getters.loggedIn)
const visibleVideos = computed(() =>
  videos.value.filter((video) => video.visible)
)

const showEditModal = ref(false)
const isEdit = ref(false)
const selectedVideo = ref(null)
const videoToPlay = ref(null)
const { showToast } = useToast()

const openNewVideoModal = () => {
  isEdit.value = false
  selectedVideo.value = {
    title: '',
    url: '',
    thumbnail: 'https://i3.ytimg.com/vi/VIDEO_ID/maxresdefault.jpg',
    visible: true,
  }
  showEditModal.value = true
}

const editVideo = (video) => {
  isEdit.value = true
  selectedVideo.value = video
  showEditModal.value = true
}

const closeModal = () => {
  showEditModal.value = false
  selectedVideo.value = null
  isEdit.value = false
}

const playVideo = (url) => {
  videoToPlay.value = url
}

const confirmDelete = (videoId) => {
  if (window.confirm('Are you sure you want to delete this video?')) {
    deleteVideo(videoId)
  }
}

const deleteVideo = async (videoId) => {
  try {
    const docRef = doc(videosCollection, videoId)
    await deleteDoc(docRef)
    showToast('Successfully Deleted Video', 'success')
  } catch (error) {
    console.error('Failed to Delete Video:', error)
    showToast('Failed to Delete Video', 'error')
  }
}
</script>

<style scoped>
.videos {
  padding-top: 3% !important;
}
.playing-text {
  top: 50%;
  transform: translateY(-50%);
}
</style>
